import * as AccountDomain from "./account-domain"
import { Chapter, Observation, LocalAuthorityChapterAccount } from "../../model"

/**
 * Compare les chapitres passés en paramètre par leur numéro (`number`).
 * @param chapter1
 * @param chapter2
 * @returns `0` si aucun des deux chapitres n'est défini ou si leurs numéro sont
 * égaux.
 *
 * `-1` si le premier chapitre est défini mais pas le second ou si
 * le numéro du second chapitre est positionné avant le premier dans l'ordre
 * alphabétique.
 *
 * `1` si le second chapitre est défini mais pas le premier ou si le
 * numéro du premier chapitre est positionné avant le second dans l'ordre
 * alphabétique.
 */
export function compareByNumber(chapter1: Chapter, chapter2: Chapter): number {
	if (chapter1 && !chapter2) return -1
	if (!chapter1 && chapter2) return 1
	if (!chapter1 && !chapter2) return 0
	if (chapter1.number === chapter2.number) return 0
	return chapter1.number > chapter2.number ? 1 : -1
}

/**
 * Vérifie si la liste de chapitres contient le chapitre passé en paramètre.
 * @param chapters
 * @param chapter
 */
export function contains(chapters: Chapter[], chapter: Chapter): boolean {
	return chapters.some(c => equal(c, chapter))
}

/**
 * Compare les identifiants des chapitre spécifiés en paramètres et renvoie un
 * booléen indiquant s'ils sont égaux ou non.
 * Si le paramètre "compareNull" est "true" (valeur par défaut), deux chapitres
 * sont non définis ("undefined" ou "null"), ils sont considérés comme égaux.
 * @param chapter1
 * @param chapter2
 * @param compareUndefined
 */
export function equal(
	chapter1: Chapter,
	chapter2: Chapter,
	compareUndefined = true
): boolean {
	if (compareUndefined && !(chapter1 || chapter2)) return true
	if (chapter1 && chapter2 && chapter1.id === chapter2.id) return true
	return false
}

export function filterChaptersByIds(
	chapters: Chapter[],
	ids: number[]
): Chapter[] {
	return chapters.filter(c => c && ids.some(id => id === c.id))
}

/**
 * Retourne la liste des chapitres d'équipement à partir de la liste passée en
 * paramètre.
 * @param chapters
 * @param receipts Si `false`, les chapitres en recette sont exclus.
 * @param expense Si `false`, les chapitres en dépense sont exclus.
 */
export function filterEquipementChapters(
	chapters: Chapter[],
	receipts: boolean = true,
	expense: boolean = true
): Chapter[] {
	return chapters.filter(chapter => {
		return isEquipment(chapter, receipts, expense)
	})
}

/**
 * Retourne la liste des chapitres filtrés selon qu'ils sont en recette et/ou
 * en dépense.
 * @param chapters
 * @param receipts
 */
export function filterChaptersByBudgetType(
	chapters: Chapter[],
	receipts: boolean
): Chapter[] {
	return chapters.filter(chapter => {
		return receipts ? isReceipts(chapter) : isExpense(chapter)
	})
}

/**
 * Retourne la liste des chapitres de financement à partir de la liste passée en
 * paramètre.
 * @param chapters
 * @param receipts Si `false`, les chapitres en recette sont exclus.
 * @param expense Si `false`, les chapitres en dépense sont exclus.
 */
export function filterFinancialChapters(
	chapters: Chapter[],
	receipts: boolean = true,
	expense: boolean = true
): Chapter[] {
	return chapters.filter(chapter => {
		return isFinancial(chapter, receipts, expense)
	})
}

/**
 * Retourne la liste des chapitres d'ordre à partir de la liste passée en
 * paramètre.
 * @param chapters
 * @param receipts Si `false`, les chapitres en recette sont exclus.
 * @param expense Si `false`, les chapitres en dépense sont exclus.
 */
export function filterOrderChapters(
	chapters: Chapter[],
	receipts: boolean = true,
	expense: boolean = true
): Chapter[] {
	return chapters.filter(chapter => {
		return isOrder(chapter, receipts, expense)
	})
}

/**
 * Retourne la liste des chapitres réels à partir de la liste passée en
 * paramètre.
 * @param chapters
 */
export function filterRealChapters(chapters: Chapter[]): Chapter[] {
	return chapters.filter(chapter => isReal(chapter))
}

/**
 * Retourne la liste des chapitres distingués attachés aux constats passés en
 * paramètre.
 * @param observations
 * @param lacas
 * @param operation Si défini, filtre les chapitres en fonction de la section
 * de chaque constat.
 * @param receipts Si défini, filtre les chapitres en fonction du type de budget
 * de chaque constat.
 */
export function getDistinctChapters(
	observations: Observation[],
	lacas: LocalAuthorityChapterAccount[],
	operation: boolean = undefined,
	receipts: boolean = undefined
): Chapter[] {
	return observations.reduce<Chapter[]>((chapters, o) => {
		const filterOperation = undefined !== operation
		&& o.account.operation !== operation

		const filterReceipts = undefined !== receipts
		&& o.account.receipts !== receipts

		if (filterOperation || filterReceipts) return chapters

		const chapter = AccountDomain.getAttachedChapter(o.account, lacas)
		if (!contains(chapters, chapter)) chapters.push(chapter)
		return chapters
	}, [])
}

/**
 * Vérifie si le chapitre passé en paramètre est rattaché à un type de budget
 * (recette et/ou dépense).
 * @param chapter
 */
export function hasBudgetType(chapter: Chapter): boolean {
	return !!(chapter && chapter.types && chapter.types.length)
}

/**
 * Vérifie si le chapitre spécifié est d'opération d'équipement.
 * @param chapter
 * @param receipts
 * @param expense
 */
export function isEquipment(
	chapter: Chapter,
	receipts: boolean = undefined,
	expense: boolean = undefined
): boolean {
	return chapter && chapter.types.some(type => {
		if (!type.operationType) return false

		const equipment = type.operationType.id === "EQUIPMENT"

		if (undefined === receipts && undefined === expense) return equipment
		if (undefined === receipts) return equipment && type.expense === expense
		if (undefined === expense) return equipment && type.receipts === receipts

		return equipment && type.receipts === receipts && type.expense === expense
	})
}

/**
 * Vérifie si le chapitre passé en paramètre est en dépense.
 * Si le chapitre n'est pas défini, il est considéré comme étant en dépense.
 * @param chapter
 * @param receipts
 * @param expense
 */
export function isExpense(chapter: Chapter): boolean {
	return !hasBudgetType(chapter) || chapter.types.some(type => type.expense)
}

/**
 * Vérifie si le chapitre spécifié est d'opération financière.
 * @param chapter
 */
export function isFinancial(
	chapter: Chapter,
	receipts: boolean = undefined,
	expense: boolean = undefined
): boolean {
	return chapter && chapter.types.some(type => {
		if (!type.operationType) return false

		const financial = type.operationType.id === "FINANCIAL"

		if (undefined === receipts && undefined === expense) return financial
		if (undefined === receipts) return financial && type.expense === expense
		if (undefined === expense) return financial && type.receipts === receipts

		return financial && type.receipts === receipts && type.expense === expense
	})
}

/**
 * * Si ni `receipts`, ni `expense` ne sont définis (par défaut), vérifie si le
 * chapitre est d'opération d'ordre, sans distinction de recette ou de dépense.
 * * Si `receipts` est défini :
 *  * Si `receipts` est `true`, vérifie si le chapitre est en recette
 * d'opération d'ordre.
 *  * Si `receipts` est `false`, vérifie si le chapitre n'est pas en recette
 * d'opération d'ordre.
 * * Si `expense` est défini :
 *   * Si `expense` est `true`, vérifie si le chapitre est en dépense
 * d'opération d'ordre.
 *   * Si `expense` est `false`, vérifie si le chapitre n'est pas en dépense
 * d'opération d'ordre.
 * @param chapter
 * @param receipts
 * @param expense
 */
export function isOrder(
	chapter: Chapter,
	receipts: boolean = undefined,
	expense: boolean = undefined
): boolean {
	return chapter && chapter.types.some(type => {
		if (!type.operationType) return false

		const order = type.operationType.id === "ORDER"

		if (undefined === receipts && undefined === expense) return order
		if (undefined === receipts) return order && type.expense === expense
		if (undefined === expense) return order && type.receipts === receipts

		return order && type.receipts === receipts && type.expense === expense
	})
}

/**
 * Vérifie si le chapitre spécifié est d'opération réelle.
 * @param chapter
 */
export function isReal(chapter: Chapter): boolean {
	return !(isEquipment(chapter)
	|| isFinancial(chapter)
	|| isOrder(chapter))
}

/**
 * Vérifie si le chapitre passé en paramètre est en recette.
 * Si le chapitre n'est pas défini, il est considéré comme étant en recette.
 * @param chapter
 */
export function isReceipts(chapter: Chapter): boolean {
	return !hasBudgetType(chapter) || chapter.types.some(type => type.receipts)
}

/**
 * Retourne une chaine sous la forme "<number> - <label>" si le chapitre existe,
 * sinon "Indéterminé".
 * @param chapter
 */
export function titlize(chapter: Chapter): string {
	return chapter ? `${chapter.number} - ${chapter.label}` : "Indéterminé"
}



// WEBPACK FOOTER //
// ./src/domain/sfp/chapter-domain.ts